/** @documentation
 * https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
 * subject to change
 * */

import { Address } from '../generated/gql-types';

interface GeocoderAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export type AddressComponent =
  | 'street_address'
  | 'route'
  | 'intersection'
  | 'political'
  | 'country'
  | 'administrative_area_level_1'
  | 'administrative_area_level_2'
  | 'administrative_area_level_3'
  | 'administrative_area_level_4'
  | 'administrative_area_level_5'
  | 'colloquial_area'
  | 'locality'
  | 'sublocality'
  | 'neighborhood'
  | 'premise'
  | 'subpremise'
  | 'plus_code'
  | 'postal_code'
  | 'natural_feature'
  | 'airport'
  | 'park'
  | 'point_of_interest'
  | 'floor'
  | 'establishment'
  | 'landmark'
  | 'parking'
  | 'post_box'
  | 'postal_town'
  | 'room'
  | 'street_number'
  | 'bus_station'
  | 'train_station'
  | 'transit_station';

export const getAddressComponent = (
  components: GeocoderAddressComponent[],
  filter: AddressComponent,
): GeocoderAddressComponent | undefined => {
  const matches = components.filter((component: GeocoderAddressComponent) =>
    component?.types.find((type: string) => type === filter),
  );

  return matches ? matches[0] : undefined;
};

export const getAddressObject = (
  components: GeocoderAddressComponent[],
): Address => {
  const subpremise =
  getAddressComponent(components, 'subpremise')?.short_name || '';
  const streetNumber =
    getAddressComponent(components, 'street_number')?.short_name || '';
  const route = getAddressComponent(components, 'route')?.short_name || '';

  // Combine unit/street number with street name, e.g. "12/345 Main Street".
  const streetNumberWithUnit = subpremise
    ? `${subpremise}/${streetNumber}`
    : streetNumber;
  const addressLine1 = [streetNumberWithUnit, route]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    addressLine1,
    addressLine2: '',
    suburb: getAddressComponent(components, 'locality')?.short_name || '',
    state:
      getAddressComponent(components, 'administrative_area_level_1')
        ?.short_name || '',
    countryLabel: getAddressComponent(components, 'country')?.long_name || '',
    countryValue: getAddressComponent(components, 'country')?.short_name || '',
    postcode: getAddressComponent(components, 'postal_code')?.long_name || '',
  };
};

export const generateFriendlyAddress = (addr: Address): string => {
  const parts = [
    addr.addressLine1,
    addr.addressLine2,
    addr.suburb,
    addr.state,
    addr.postcode,
    addr.countryLabel,
  ].filter(Boolean); // Remove empty/null/undefined values

  return parts.join(', ');
};
